import {AnimationGlobalEnum} from './animation-global.enum';

//#region ENUM AnimationTimingFunctionsEnum

/**
 * ### AnimationTimingFunctionsEnum
 * Enum representing possible values for the CSS `animation-timing-function` property.
 *
 * This enum is accompanied by:
 * - `AnimationTimingFunctionsEnumHumanReadable` — human-friendly labels.
 * - `AnimationTimingFunctionsEnumType` — a union type of all enum keys.
 *
 * @enum AnimationTimingFunctionsEnum
 * @property EASE - A standard easing function with slow start, fast middle, and slow end.
 * @property EASE_IN - Animation accelerates from zero velocity.
 * @property EASE_OUT - Animation decelerates to zero velocity.
 * @property EASE_IN_OUT - Animation accelerates and decelerates.
 * @property LINEAR - Animation progresses at a constant pace.
 * @property STEP_END - Steps timing function that jumps to the end of the step immediately.
 */
export enum AnimationTimingFunctionsEnum {
  /** A standard easing function with slow start, fast middle, and slow end. */
  EASE = 'ease',

  /** Animation accelerates from zero velocity. */
  EASE_IN = 'ease-in',

  /** Animation decelerates to zero velocity. */
  EASE_OUT = 'ease-out',

  /** Animation accelerates and decelerates. */
  EASE_IN_OUT = 'ease-in-out',

  /** Animation progresses at a constant pace. */
  LINEAR = 'linear',

  /** Steps timing function that jumps to the end of the step immediately. */
  STEP_END = 'step-end',
}

//#endregion

//#region TYPE AnimationTimingFunctionsEnumType
/**
 * ### AnimationTimingFunctionsEnumType
 * Union type of all keys in `AnimationTimingFunctionsEnum`.
 *
 * @type AnimationTimingFunctionsEnumType
 * @example
 * // Possible values: 'EASE' | 'EASE_IN' | 'EASE_OUT' | 'EASE_IN_OUT' | 'LINEAR' | 'STEP_END'
 */
export type AnimationTimingFunctionsEnumType = keyof typeof AnimationTimingFunctionsEnum;
//#endregion

//#region TYPE AnimationTimingFunctionsType
/**
 * ### AnimationTimingFunctionsType
 * Union type for CSS `animation-timing-function` values.
 *
 * This type includes:
 * - Any string value (to allow custom or unlisted timing functions).
 * - Keys from `AnimationTimingFunctionsEnum` (predefined timing functions).
 * - Keys from `AnimationGlobalEnum` (global CSS animation keywords).
 *
 * @type AnimationTimingFunctionsType
 * @example
 * // Possible values: 'ease' | 'ease-in' | 'linear' | 'inherit' | 'initial' | 'custom-function'
 */
export type AnimationTimingFunctionsType =
  | string
  | keyof typeof AnimationTimingFunctionsEnum
  | keyof typeof AnimationGlobalEnum;
//#endregion
